//	The page: one state object, two renders (game list, board), a 30 s
//	refresher. Deep link: #<fileBasename> picks the game. ?screen=event
//	keeps refreshing even when the tab is hidden (the venue TV).
import {fetchGames, fetchBoard, fetchLocations} from './api.js';
import {applyFilter} from './format.js';
import {createRefresher} from './refresh.js';
import * as render from './render.js';

const state = {
	games: [],
	game: null,
	page: 1,
	filters: {country: '', state: '', city: ''},
	board: null,
	//	the board's top 3, kept for the podium on pages after the first
	top3: [],
	//	every place with a score on this game's board — feeds the filter dropdowns
	locations: [],
	error: null,
	stale: false,
};

const $games = document.getElementById('games');
const $board = document.getElementById('board');
const eventScreen = new URLSearchParams(location.search).get('screen') === 'event';

//	Re-render only what changed, and keep already-painted avatar <img>
//	elements alive across a re-render: a recreated element repaints a frame
//	late and blinks, a moved one does not.
const lastHTML = new WeakMap();
function setHTML(el, html) {
	if (lastHTML.get(el) === html) return;
	const keep = new Map([...el.querySelectorAll('.av img')].map((img) => [img.src, img]));
	el.innerHTML = html;
	lastHTML.set(el, html);
	el.querySelectorAll('.av img').forEach((img) => {
		const old = keep.get(img.src);
		if (old) img.replaceWith(old);
	});
}

function paint() {
	setHTML($games, render.games({games: state.games, currentID: state.game?.gameVersionID}));
	setHTML($board, render.board(state));
	measure();
}

async function loadBoard() {
	if (!state.game || !state.game.players) {
		state.board = null;
		paint();
		return;
	}
	try {
		const query = {gameVersionID: state.game.gameVersionID, filters: state.filters};
		const [data, top] = await Promise.all([
			fetchBoard({...query, page: state.page}),
			//	later pages don't carry the leaders; a 3-row page 1 does (cached server-side)
			state.page > 1 ? fetchBoard({...query, page: 1, count: 3}) : null,
		]);
		state.board = data;
		state.top3 = top ? top.rows : data.rows.slice(0, 3);
		state.error = null;
		state.stale = false;
	} catch (err) {
		//	keep the last board on screen; the live tag says we are retrying
		state.error = err.message;
		state.stale = true;
	}
	paint();
}

//	Fetched with each refresh so a new city shows up in the dropdowns within
//	30 s; a failure keeps the last list (the board is what matters).
async function loadLocations() {
	if (!state.game || !state.game.players) {
		state.locations = [];
		return;
	}
	try {
		state.locations = await fetchLocations(state.game.gameVersionID);
		//	the board may have painted first; the dropdowns need this pass too
		paint();
	} catch (err) {
		//	keep the previous options
	}
}

async function loadGames() {
	try {
		state.games = await fetchGames();
		state.error = null;
	} catch (err) {
		state.error = err.message;
	}
	const wanted = decodeURIComponent(location.hash.slice(1));
	state.game =
		state.games.find((g) => g.fileBasename === wanted) ||
		state.games.find((g) => g.gameVersionID === state.game?.gameVersionID) ||
		state.games[0] ||
		null;
}

const refresher = createRefresher({
	always: eventScreen,
	tick: async () => {
		await loadGames();
		await Promise.all([loadLocations(), loadBoard()]);
	},
});

function selectGame(id) {
	const game = state.games.find((g) => g.gameVersionID === id);
	if (!game) return;
	state.game = game;
	state.page = 1;
	state.filters = {country: '', state: '', city: ''};
	state.locations = [];
	state.board = null;
	history.replaceState(null, '', `#${game.fileBasename}`);
	window.scrollTo({top: 0});
	paint();
	//	a new game needs its board and its filter options; the game list is current
	Promise.all([loadLocations(), loadBoard()]).then(() => refresher.restart());
}

//	Page or filter changed: only the board moves.
function reloadBoard() {
	loadBoard().then(() => refresher.restart());
}

document.addEventListener('click', (e) => {
	const g = e.target.closest('[data-game]');
	if (g) return selectGame(Number(g.dataset.game));
	const p = e.target.closest('[data-page]');
	if (p && !p.disabled) {
		state.page = Number(p.dataset.page);
		window.scrollTo({top: 0});
		return reloadBoard();
	}
	if (e.target.closest('[data-filter-clear]')) {
		state.filters = {country: '', state: '', city: ''};
		state.page = 1;
		return reloadBoard();
	}
	return undefined;
});

document.addEventListener('change', (e) => {
	const sel = e.target.closest('[data-filter]');
	if (!sel) return;
	state.filters = applyFilter(state.filters, sel.dataset.filter, sel.value);
	state.page = 1;
	reloadBoard();
});

document.addEventListener('visibilitychange', () => {
	if (document.visibilityState === 'visible') refresher.resume();
});

window.addEventListener('hashchange', () => {
	const wanted = decodeURIComponent(location.hash.slice(1));
	const game = state.games.find((g) => g.fileBasename === wanted);
	if (game && game !== state.game) selectGame(game.gameVersionID);
});

//	Sticky stack: each pinned strip sits just under the one above it. The
//	heights are measured, not assumed, so every breakpoint gets it right.
const root = document.documentElement.style;
const h = (el) => (el && el.getClientRects().length ? el.getBoundingClientRect().height : 0);
function measure() {
	const banner = h(document.querySelector('.banner')) - 1;
	const top = banner + h(document.querySelector('.top')) - 1;
	const side = document.querySelector('.side');
	//	only a pinned game list (phone strip) sits under the header; a scrolling one does not
	const sideStyle = getComputedStyle(side);
	const sideH = sideStyle.position === 'sticky' && sideStyle.display === 'flex' ? h(side) - 1 : 0;
	const head = top + sideH + h(document.querySelector('.head')) - 1;
	root.setProperty('--h-banner', `${Math.max(0, banner)}px`);
	root.setProperty('--h-top', `${Math.max(0, top)}px`);
	root.setProperty('--h-side', `${Math.max(0, top + sideH)}px`);
	root.setProperty('--h-head', `${Math.max(0, head)}px`);
	pinCheck();
}
function pinCheck() {
	const podium = document.querySelector('.podium');
	const line = parseFloat(root.getPropertyValue('--h-head')) || 0;
	const gone = !!podium && podium.getBoundingClientRect().bottom < line + 4;
	document.querySelector('.frame').classList.toggle('pin3', gone);
	const strip = document.querySelector('.top3');
	root.setProperty('--h-strip', gone ? `${Math.max(0, h(strip) - 1)}px` : '0px');
}
window.addEventListener('scroll', pinCheck, {passive: true});
window.addEventListener('resize', measure);
if (document.fonts?.ready) document.fonts.ready.then(measure);

paint();
refresher.start();
refresher.now();
